<template>
  <el-table
    :data="pickups"
    stripe
    :size="tableSize"
  >
    <el-table-column
      v-if="!!$scopedSlots['peek']"
      type="expand"
    >
      <template v-slot="{ row }">
        <slot
          name="peek"
          :pickup="row"
        />
      </template>
    </el-table-column>

    <el-table-column
      label="Booking Ref."
      :width="120"
    >
      <template v-slot="{ row }">
        <slot
          name="reference"
          :pickup="row"
        >
          {{ row.booking_reference }}
        </slot>

        <template v-if="isPickup(row.previous_pickup)">
          <font-awesome-icon
            v-tooltip="`Rebooking of Pickup ${row.previous_pickup.booking_reference}`"
            :icon="['fal', 'repeat']"
            fixed-width
            class="text-info pl-1"
          />
        </template>

        <template v-if="(row.account && row.sender) && row.account.name !== row.sender.organisation_name">
          <font-awesome-icon
            v-tooltip="'Third Party Pickup'"
            :icon="['fal', 'random']"
            fixed-width
            class="text-info pl-1"
          />
        </template>

        <template v-if="row.caller_name === 'OPEN360'">
          <font-awesome-icon
            v-tooltip="'Created by OPEN360'"
            :icon="['fal', 'external-link-square']"
            fixed-width
            class="text-info pl-1"
          />
        </template>
      </template>
    </el-table-column>

    <el-table-column
      label="Carrier"
      prop="carrier"
      sortable
      :sort-method="sortCarrier"
      :width="200"
      show-overflow-tooltip
      :formatter="(row, col, cellValue) => cellValue ? (cellValue.name || '-') : '-'"
    />

    <el-table-column
      label="Account"
      prop="account"
      sortable
      :sort-method="sortAccount"
      :width="200"
      show-overflow-tooltip
      :formatter="(row, col, cellValue) => cellValue ? (cellValue.name || '-') : '-'"
    />

    <el-table-column
      label="Sender"
      prop="sender"
      :width="250"
    >
      <template slot-scope="scope">
        <template v-if="scope.row.sender">
          <v-popover
            placement="top"
            trigger="hover"
          >
            <template slot="popover">
              <!-- eslint-disable vue/no-v-html -->
              <span
                class="text-uppercase"
                v-html="$filters.address(scope.row.sender, true)"
              />
              <!-- eslint-enable vue/no-v-html -->
            </template>

            <u class="text-uppercase">
              {{ scope.row.sender.organisation_name | truncate(25) }}
            </u>
          </v-popover>
        </template>

        <template v-else>
          -
        </template>
      </template>
    </el-table-column>

    <el-table-column
      label="Receiver"
      prop="receiver"
      :width="250"
    >
      <template slot-scope="scope">
        <template v-if="scope.row.receiver">
          <template v-if="scope.row.receiver.organisation_name === 'VARIOUS'">
            {{ scope.row.receiver.organisation_name }}
          </template>

          <template v-else>
            <v-popover
              placement="top"
              trigger="hover"
            >
              <template slot="popover">
                <!-- eslint-disable vue/no-v-html -->
                <span
                  class="text-uppercase"
                  v-html="$filters.address(scope.row.receiver, true)"
                />
                <!-- eslint-enable vue/no-v-html -->
              </template>

              <u class="text-uppercase">
                {{ scope.row.receiver.organisation_name | truncate(25) }}
              </u>
            </v-popover>
          </template>
        </template>

        <template v-else>
          -
        </template>
      </template>
    </el-table-column>

    <el-table-column
      label="Booked At"
      prop="created_at"
      sortable
      :width="160"
      :formatter="(row, col, cellValue) => cellValue ? $moment(cellValue).format('DD/MM/YYYY hh:mm a') : '-'"
    />

    <el-table-column
      label="Pickup Date"
      prop="customer_pickup_date"
      :width="120"
      :formatter="(row, col, cellValue) => cellValue ? $moment(cellValue).format('DD/MM/YYYY') : '-'"
    />

    <el-table-column
      label="Ready At"
      prop="customer_ready_time"
      :width="120"
      :formatter="(row, col, cellValue) => cellValue ? (($moment(cellValue, 'HH:mm').format('hh:mm a')) || '-') : '-'"
    />

    <el-table-column
      label="Closing At"
      prop="customer_closing_time"
      :width="120"
      :formatter="(row, col, cellValue) => cellValue ? (($moment(cellValue, 'HH:mm').format('hh:mm a')) || '-') : '-'"
    />

    <el-table-column
      label="Status"
      prop="status"
      :width="120"
    >
      <template slot-scope="scope">
        <el-tag
          v-tooltip="getStatusTagTooltip(scope.row)"
          :type="getTagByStatus(scope.row.status)"
          size="mini"
        >
          {{ scope.row.status | titleize }}
        </el-tag>
      </template>
    </el-table-column>

    <el-table-column
      v-if="!!$scopedSlots['actions']"
      label="Actions"
      fixed="right"
      align="right"
      :min-width="130"
    >
      <template v-slot="{ row }">
        <slot
          name="actions"
          :pickup="row"
        />
      </template>
    </el-table-column>
  </el-table>
</template>

<script>
import { statusTag } from '@/components/Application/CustomerService/Pickups/Pickup/PickupFunctions'

export default {
  props: {
    pickups: {
      type: Array,
      required: true
    }
  },
  computed: {
    tableSize () {
      let configuration = this.$_.cloneDeep(this.$store.getters.configuration)
      if (this.$_.isEmpty(configuration.layout.tableSize)) {
        return 'default'
      } else {
        return configuration.layout.tableSize
      }
    }
  },
  methods: {
    isPickup (pickup) {
      return pickup instanceof this.$sdk.pickup
    },
    sortCarrier (a, b) {
      return a.carrier.name.localeCompare(b.carrier.name)
    },
    sortAccount (a, b) {
      return a.account.name.localeCompare(b.account.name)
    },
    formatAddressName (address) {
      if (address) {
        const fields = ['address_line1', 'address_line2', 'locality', 'postal_code', 'administrative_area', 'country']
        let stringList = []
        fields.map(field => {
          stringList.push(address[field] || '-')
        })
        stringList = stringList.filter(s => !(['-', ' ', '.']).includes(s))
        return stringList.filter(Boolean).join(', ')
      }

      return '-'
    },
    getStatusTagTooltip (pickup) {
      let tooltip

      if (pickup.status === 'error') {
        tooltip = pickup.carrier_response
      } else if (pickup.status === 'confirmed') {
        tooltip = pickup.carrier_reference
      } else if (pickup.status === 'held') {
        tooltip = pickup.carrier_response
      }

      return tooltip
    },
    getTagByStatus (status) {
      return statusTag(status)
    }
  }
}
</script>
